import { useRef, useState } from 'react';
import type { FileKind } from '../types/api';

interface Props {
  kind: FileKind;
  icon: string;
  label: string;
  description: string;
  required: boolean;
  uploaded: boolean;
  onUpload: (kind: FileKind, file: File) => Promise<void>;
}

export default function FileUploadCard({ kind, icon, label, description, required, uploaded, onUpload }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  async function handle(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setErr(null);
    try {
      await onUpload(kind, f);
      setFileName(f.name);
    } catch (ex: any) {
      setErr(ex?.response?.data?.detail || String(ex));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <div className="card p-4 relative">
      <div className="flex items-start gap-3">
        <div className="text-2xl">{icon}</div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white font-semibold text-sm truncate">{label}</span>
            {required
              ? <span className="badge bg-rose-500/20 text-rose-300">obrigatório</span>
              : <span className="badge bg-mbc-blue/20 text-mbc-bluel">opcional</span>}
          </div>
          <div className="text-[11px] text-mbc-mute mt-0.5">{description}</div>
        </div>
      </div>

      <label className="mt-3 flex items-center justify-center gap-2 border border-dashed border-mbc-line hover:border-mbc-orange rounded-xl py-3 cursor-pointer text-xs text-mbc-mute transition">
        <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handle} disabled={busy} />
        <span>{busy ? '⏳ enviando…' : '📎 Selecionar arquivo'}</span>
      </label>

      <div className="mt-2 text-[11px]">
        <span className={uploaded ? 'text-emerald-400' : 'text-mbc-mute'}>
          {uploaded ? `✓ ${fileName || 'arquivo enviado'}` : '—'}
        </span>
      </div>

      {err && <p className="mt-1 text-xs text-rose-300">{err}</p>}
    </div>
  );
}
